import type { PickupLocation } from "../transporter/transporter";
import type { Vehicle } from "../inventory/inventory";
import {
  Lead,
  LeadShippingLocationTruck,
  normalizeLeadTruckDropoffLocationIds,
} from "./lead";

/** Street, city, state, and ZIP must all be present. */
export const isPickupLocationAddressComplete = (
  location: PickupLocation | undefined | null,
): boolean => {
  if (!location) return false;
  return Boolean(
    location.address?.trim() &&
      location.city?.trim() &&
      location.state?.trim() &&
      location.zip?.trim(),
  );
};

export const getLeadDropoffLocation = (
  lead: Lead,
  pickupLocations: PickupLocation[] | undefined,
  truckId: string,
): PickupLocation | undefined => {
  const map = normalizeLeadTruckDropoffLocationIds(lead.truckDropoffLocationIds);
  const id = map[truckId]?.trim();
  if (!id) return undefined;
  return pickupLocations?.find((location) => location.id === id);
};

export const getTruckShippingLocationNeeds = (
  vehicle: Vehicle,
  dropoff: PickupLocation | undefined,
): { missingOrigin: boolean; missingDropoff: boolean } => {
  const missingOrigin = !isPickupLocationAddressComplete(vehicle.pickup_location);
  const missingDropoff = !isPickupLocationAddressComplete(dropoff);
  return { missingOrigin, missingDropoff };
};

export const getTrucksNeedingShippingLocations = (
  lead: Lead,
  vehicles: Vehicle[],
  pickupLocations: PickupLocation[] | undefined,
): LeadShippingLocationTruck[] => {
  const truckIds = new Set(lead.truckIds ?? []);
  const results: LeadShippingLocationTruck[] = [];

  vehicles.forEach((vehicle) => {
    if (!truckIds.has(vehicle.id)) return;

    const dropoff = getLeadDropoffLocation(lead, pickupLocations, vehicle.id);
    const { missingOrigin, missingDropoff } = getTruckShippingLocationNeeds(
      vehicle,
      dropoff,
    );

    if (!missingOrigin && !missingDropoff) return;

    results.push({
      vehicleId: vehicle.id,
      stockNumber: vehicle.stock_number ?? null,
      year: vehicle.year ?? null,
      make: vehicle.make ?? null,
      model: vehicle.model ?? null,
      vin: vehicle.vin ?? null,
      missingOrigin,
      missingDropoff,
    });
  });

  return results;
};

export const allLeadTrucksHaveShippingLocations = (
  lead: Lead,
  vehicles: Vehicle[],
  pickupLocations: PickupLocation[] | undefined,
): boolean =>
  getTrucksNeedingShippingLocations(lead, vehicles, pickupLocations).length === 0;
